// @flow
import jwtDecode from 'jwt-decode';
import memoize from 'lru-memoize';
import AuthState from 'bfu-auth-client/src/auth/AuthState.js';
/*

Auth.js is a class that is typically instanciated once on the window
and persists for the lifetime of the window.

- It handles storing the state of the viewers sign in / the JWT
- It handles refreshing an access token using a refresh token
- It re-requests new access tokens preemptively based on the TTL of the access token
- It stores and retrieves this state in local storage if available
- It provides fetch() and fetchGraphql() functions (wrappers around fetch()) that add the JWT to the headers
- If provides methods to interact with the auth-lambda (signIn, forgotPassword etc)
- It performs a redirect upon sign-in if "?redirectPath=" is set in query params
- If pulls accessToken, idToken and/or refreshToken from query params if provided ("?accessToken=" etc)

*/

type State = {
    accessToken: ?string,
    refreshToken: ?string,
    idToken: ?string,
    tokenExpiry: number,
    tenantId: ?string
};

type Tokens = {
    accessToken: string,
    refreshToken: string,
    idToken: string
};

type IdTokenIdentity = {
    dateCreated: string,
    userId: string,
    providerName: string,
    providerType: string,
    issuer: string,
    primary: string
};

type OnChangeFunction = (state: State) => void;

export default class Auth {

    authLambda: string = '';
    redirectPathEnabled: boolean = false;
    authState: AuthState<State>;
    internalPaths: Array<string> = [
        '/auth0Landing',
        '/logout',
        '/signup',
        '/signUp',
        '/sign-up',
        "/signin",
        '/confirm',
        '/forgotPassword'
    ];

    constructor() {

        // check if this is a logout page
        // and if so, clear the auth state


        const urlParams = new URLSearchParams(window.location.search);
        const accessToken = urlParams.get('accessToken');
        const refreshToken = urlParams.get('refreshToken');
        const idToken = urlParams.get('idToken');
        const redirectPath = urlParams.get('redirectPath');
        const initialState = {
            ...(refreshToken ? {refreshToken} : {}),
            ...(accessToken ? {accessToken} : {}),
            ...(idToken ? {idToken} : {})
        };

        this.authState = new AuthState({
            initialState
        });

        if(!this.internalPaths.includes(window?.location?.pathname)) {
            this.authState.merge({
                clientPath: window.location.pathname
            });
        }
        if(redirectPath) {
            this.authState.merge({
                redirectPath
            });
        }
        if(window.location.pathname === '/logout') {
            this.clearAuth().then(() => {
                window.location.href = `${window.location.protocol}//${window.location.hostname}/`;
            });
        }
    }

    //
    // private methods
    //

    _now = () => Math.floor(Date.now() / 1000);

    _getAuthStateWithValidAccessToken = async (): Promise<State> => {
        let authState = this.authState.get();

        if(this._now() > authState.tokenExpiry) {
            return this.refreshToken();
        }
        return authState;
    };

    _authPost = async (path: string, query: {[key: string]: any} = {}, returnRawResponse: ?boolean = false): Promise<any> => {
        let url = `${this.authLambda}${path}`;
        let {accessToken, tenantId} = this.authState.get();

        let response = await fetch(url, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${accessToken}`,
                'x-blueflag-tenant-id': tenantId
            },
            body: JSON.stringify(query)
            // keepalive: true
        });
        if(returnRawResponse) {
            return response;
        }

        if(!response.ok) {
            throw await response.json();
        }

        return response.json();
    }

    _mergeState = (newState: {[key: string]: any}): Promise<State> => {
        let {accessToken} = newState;

        if(accessToken) {
            let {exp, iat} = jwtDecode(accessToken);
            let ttl = exp - iat;
            let now = this._now();
            let tokenExpiry = now + (ttl * 0.75);
            // ^ multiply ttl by 0.75
            // so that we're slightly eager in requesting new access tokens
            // and definitely dont keep one that expires
            setTimeout(() => {
                if(this.authState.get().isSignedIn){
                    this.refreshToken();
                }
            }, ttl * 0.75 * 1000);

            newState = {
                ...newState,
                tokenExpiry
            };
        }

        return this.authState.merge(newState);
    };

    _postSignIn = (): boolean => {
        const redirectPath = this.authState.get().redirectPath;
        const clientPath = this.authState.get().clientPath;
        // console.log('redirectPath', redirectPath, this.redirectPathEnabled);

        if (clientPath && clientPath !== window.location.pathname) {
            if (!clientPath.startsWith('/')) {
                const clientPath = `/${clientPath}`;
            }
            let url = new URL(clientPath, window.location.origin);
            window.location.href = url.toString();
        }
        if (this.redirectPathEnabled && redirectPath) {
            this.authState.merge({redirectPath: undefined});
            return this._redirect(redirectPath);
        }

        return false;
    };

    // New helper method for redirecting
    _redirect = (path: string): boolean => {
        if (this._tokenRedirect) {
            this._tokenRedirect(this.authState.get(), path);
        } else {
            window.location.href = path;  // For cases where no _tokenRedirect method is defined
        }
        return true;
    };



    // Token redirect to the cloudfront distribution
    _tokenRedirect = async (tokens: Tokens, endpoint: string): boolean => {

        let url = new URL(`https://${window.location.host}${endpoint}`);

        let params = new URLSearchParams(url.search);
        params.append('accessToken', tokens.accessToken);
        params.append('idToken', tokens.idToken);
        params.append('refreshToken', tokens.refreshToken);
        url.search = params.toString();
        window.location.href = url.toString();
        return true;
    };

    _getViewerFromToken = memoize(1)((accessToken) => {
        if(!accessToken) {
            return;
        }
        return jwtDecode(accessToken).viewer;
    });

    _idTokenIdentity = (state): IdTokenIdentity => {
        const {idToken} = state;
        if (idToken) {
            const {identities} = jwtDecode(idToken);
            if (Array.isArray(identities) && identities.length > 0) {
                return identities[0];
            }
        }
        return undefined;
    };

    //
    // public properties
    //

    get state(): State {
        return this.authState.get();
    }

    get viewer(): any {
        return this._getViewerFromToken(this.authState.get().accessToken);
    }

    get isSignedIn(): ?boolean {
        return !!this.authState.get().accessToken;
    }


    //
    // public methods
    //

    //
    // Return the authentication endpoints.
    //

    getAuthEndpoint = async(): Promise<any> => {
        let {tenantId, authEndpoint} = this.authState.get();
        const {location} = window;
        if(authEndpoint !== null){
            return this.authState.get();
        }
        let url = new URL(`${this.authLambda}/authorizationUrl`);
        url.searchParams.append("redirect_uri", `${location.protocol}//${location.hostname}/auth0Landing`);
        url.searchParams.append("logout_uri", `${location.protocol}//${location.hostname}/logout`);

        let response = await fetch(url, {
            headers: {
                'Content-Type': 'application/json',
                'x-blueflag-tenant-id': tenantId
            }

        });
        const json = await response.json();
        return this.authState.merge({
            authEndpoint: json.data
        });


    }


    //
    // set auth lambda url
    //

    setAuthLambdaUrl = async (authLambda: string): Promise<any> => {
        this.authLambda = authLambda;
    };

    //
    // set tenant id
    //

    setTenantId = async (tenantId: string): Promise<any> => {
        return this._mergeState({
            tenantId
        });
    };

    //
    // use redirect path
    //

    enableRedirectPath = (use: boolean = true) => {
        this.redirectPathEnabled = use;
        this.isSignedIn && this._postSignIn(this.authState.get());
    };

    //
    // fetch
    // just like fetch, but also adds headers and handles token refreshes
    //

    fetch = async (url: string, options: {[key: string]: any} = {}): Promise<any> => {
        let {accessToken} = await this._getAuthStateWithValidAccessToken();

        let headers = {
            'Authorization': `Bearer ${accessToken}`,
            'X-Blueflag-Service-Name': 'possum-client',
            ...(options.headers || {})
        };

        return await fetch(url, {
            ...options,
            headers
        });
    };

    //
    // fetchGraphql
    // a convenience method for sending graphql requests
    // sends a BlueflagAuth.fetch() request with:
    // - POST method
    // - valid access token
    // - json body
    // - json response
    //

    fetchGraphql = async (url: string, payload: {[key: string]: any}, additionalOptions: {[key: string]: any} = {}): Promise<any> => {
        let {headers, ...options} = additionalOptions;

        let response = await this.fetch(url, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'X-Blueflag-Service-Name': 'possum-client',
                ...(headers || {})
            },
            body: JSON.stringify(payload),
            ...options
        });

        if(!response.ok) {
            throw response;
        }
        return response.json();
    };

    //
    // refresh token
    //

    refreshToken = async (): Promise<any> => {
        let {refreshToken} = this.authState.get();

        let response = await this._authPost('/refreshToken', {refreshToken}, true);

        if(response.status === 401) {
            this.signOut();
            throw response;
        }

        let payload = await response.json();

        return this._mergeState({
            accessToken: payload.accessToken,
            idToken: payload.idToken
        });
    }

    //
    // sign in with user/pass cognito
    //

    signIn = async (username: string, password: string): Promise<any> => {
        let response = await this._authPost('/signIn', {
            username,
            password
        });

        this._mergeState({
            accessToken: response.accessToken,
            idToken: response.idToken,
            refreshToken: response.refreshToken,
            isSignedIn: true
        });
        this._postSignIn(response);
    }


    //
    // sign in with auth0 cognito
    //

    signInAuthCode = async (): Promise<any> => {

        // Grab the code from the url/
        const urlParams = new URLSearchParams(window.location.search);
        const code = urlParams.get('code');

        if (!code) throw new Error("Missing code url param.");
        let response = await this._authPost('/signInAuthCode', {
            code,
            redirect_uri: `${window.location.protocol}//${window.location.hostname}/auth0Landing`
        });
        this._mergeState({
            accessToken: response.accessToken,
            idToken: response.idToken,
            refreshToken: response.refreshToken,
            isSignedIn: true
        });
        this._postSignIn(response);
    }

    //
    // sign out
    // signs out of the current window by forgetting all tokens
    //

    signOut = async (): Promise<any> => {
        await this.getAuthEndpoint();
        if(this.state.authEndpoint) {
            let url = new URL(this.state.authEndpoint.logout_endpoint);
            url.searchParams.append('redirect_uri', `${window.location.protocol}//${window.location.hostname}/auth0Landing`);
            url.searchParams.append('response_type', `code`);
            url.searchParams.set("logout_uri", `${location.protocol}//${location.hostname}/logout`);
            window.location.href = url.toString();
        } else {
            window.location.href = `${window.location.protocol}//${window.location.hostname}/logout`;
        }
        await this.signOutIdp();
    }

    clearAuth = async (): Promise<any> => {
        return this._mergeState({
            accessToken: '',
            idToken: '',
            refreshToken: ''
        });
    }

    //
    // sign out global
    // signs out of all windows
    // This method is broken, it does not work
    //

    signOutGlobal = async (): Promise<any> => {
        await this._authPost('/signOutGlobal');
        return this.clearAuth();
    }

    // sign out idp
    signOutIdp = async (): Promise<any> => {
        const identity: IdTokenIdentity =
            this._idTokenIdentity(this.authState.get());
        if (identity && this._isIdpClerk(identity)) {
            await this._authPost('/signOutClerk', {
                userId: identity.userId
            });
        }
    }

    _isIdpClerk = (identity: IdTokenIdentity): boolean => {
        return identity.providerName.startsWith("EXTERNAL-")
            && identity.userId.toLowerCase().startsWith("user_");
    }

    //
    // sign up
    // begin process of becoming a user of the app
    //

    signUp = async (username: string, password: string, attributes: {[key: string]: any}): Promise<any> => {
        let response = await this._authPost('/signUp', {
            username,
            password,
            attributes
        });

        return this._mergeState({
            accessToken: response.accessToken,
            idToken: response.idToken,
            refreshToken: response.refreshToken
        });
    }

    //
    // sign up confim
    // confirms a registered, unauthenticated user
    //

    signUpConfirm = async (username: string, verificationCode: string): Promise<any> => {
        return this._authPost('/signUpConfirm', {
            username,
            verificationCode: verificationCode.trim()
        });
    }

    //
    // sign up confim resend
    // resends a confirmation code that confirms the registration for an unauthenticated user
    //

    signUpConfirmResend(username: string): Promise<any> {
        return this._authPost('/signUpConfirmResend', {
            username
        });
    }

    //
    // forgot password request
    // sends a request to reset password
    //

    forgotPasswordRequest = async (username: string): Promise<any> => {
        return this._authPost('/forgotPasswordRequest', {
            username
        });
    }

    //
    // forgot password confirm
    // after a user sets their new password, this confirms it
    //

    forgotPasswordConfirm = async (username: string, password: string, confirmationCode: string): Promise<any> => {
        return this._authPost('/forgotPasswordConfirm', {
            username,
            confirmationCode: confirmationCode.trim(),
            password
        });
    }

    //
    // onChange
    // registers an onChange callback
    //

    onChange = (callback: OnChangeFunction) => {
        this.authState.onChange(callback);
    };
}
